import {
  AllowedOAuthFlow,
  AuthClientItem,
  useSnackbarNotification,
} from '@jupiterone/jupiter-web-toolkit';
import useAuthClients from './useAuthClients';
import { CircularProgress } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import styles from 'navbar/src/jupiterAccountSettings/AccountSettings/AccountPage.styles';
import AccountPageList from 'navbar/src/jupiterAccountSettings/AccountSettings/AccountPageList/AccountPageList';
import AuthClientDetail from './AuthClientDetail/AuthClientDetail';
import AuthClientListItem from './AuthClientListItem/AuthClientListItem';

interface Props {
  classes: any;
}

const AuthenticationClientsPage = ({ classes }: Props) => {
  const notification = useSnackbarNotification();
  const {
    createAuthClient,
    deleteAuthClient,
    getAuthClients,
    updateAuthClient,
  } = useAuthClients();
  const [loading, setLoading] = useState<boolean>(true);

  const newAuthClientItem: AuthClientItem = {
    id: '',
    name: '',
    callbackUrls: [],
    logoutUrls: [],
    identityProviders: [],
    allowedOAuthFlows: [AllowedOAuthFlow.CODE, AllowedOAuthFlow.IMPLICIT],
  };

  const [authClient, setAuthClient] = useState<AuthClientItem>(
    newAuthClientItem
  );

  const authClientEditCopyState = useState<AuthClientItem>(newAuthClientItem);
  const [authClientEditCopy, setAuthClientEditCopy] = authClientEditCopyState;

  useEffect(() => {
    const fetchAuthClientData = async () => {
      try {
        const response = await getAuthClients();
        if (response.items.length > 0) {
          setAuthClient(response.items[0]);
        }
      } catch (err) {
        notification.error({
          message: 'Failed to fetch auth clients',
          error: err,
        });
      } finally {
        setLoading(false);
      }
    };

    void fetchAuthClientData();
  }, []);

  const createClient = async () => {
    try {
      const client = await createAuthClient(authClientEditCopy);
      setAuthClient(client);
      notification.success('Successfully created client!', 2500);
    } catch (error) {
      notification.error({
        message: 'Failed to create auth client',
        autoHideDuration: 2500,
        error,
      });
    }
  };

  const updateClient = async () => {
    try {
      await updateAuthClient(authClientEditCopy);
      setAuthClient(authClientEditCopy);
      notification.success('Successfully updated client!', 2500);
    } catch (error) {
      notification.error({
        message: 'Failed to update auth client',
        autoHideDuration: 2500,
        error,
      });
    }
  };

  const saveClient = async () => {
    if (!authClientEditCopy.id) {
      await createClient();
    } else {
      await updateClient();
    }
  };

  const deleteClient = async () => {
    try {
      await deleteAuthClient(authClient.id);
      setAuthClient(newAuthClientItem);
      notification.success('Successfully deleted client!', 2500);
    } catch (err) {
      notification.error({
        message: 'Failed to delete auth client',
        autoHideDuration: 2500,
        error: err,
      });
    }
  };

  // auth client detail window open
  const [openAuthClientDetail, setOpenAuthClientDetail] = useState<boolean>(
    false
  );

  const handleEditAuthClientDetail = () => {
    setAuthClientEditCopy(authClient);
    setOpenAuthClientDetail(true);
  };

  const handleCloseAuthClientDetail = () => {
    setAuthClientEditCopy(newAuthClientItem);
    setOpenAuthClientDetail(false);
  };

  if (loading) {
    return (
      <div className={classes.spinnerCenter}>
        <CircularProgress />
      </div>
    );
  }

  if (openAuthClientDetail) {
    return (
      <AuthClientDetail
        authClientState={authClientEditCopyState}
        onBack={handleCloseAuthClientDetail}
        onDelete={deleteClient}
        onSave={saveClient}
      />
    );
  }

  return (
    <AccountPageList<AuthClientItem>
      classes={classes}
      titleText="Single Sign On"
      addText="Configure"
      items={authClient.id ? [authClient] : []}
      itemRenderer={(input) => <AuthClientListItem {...input} />}
      onAdd={handleEditAuthClientDetail}
      onSelect={handleEditAuthClientDetail}
    />
  );
};

export default withStyles(styles)(AuthenticationClientsPage);
export { AuthenticationClientsPage as AuthenticationClientsPageUnwrapped };
